import React, { createContext, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { useChallengeRewards } from './hooks/useChallengeRewards';
import { useChallenges } from './hooks/useChallenges';
import { useRewards } from './hooks/useRewards';

// ────────────────────────────────────────────────────────────────────────────────

const ChallengesAndRewardsContext = createContext<
  ChallengesAndRewardsContextType | undefined
>(undefined);

export const ChallengesAndRewardsProvider = (
  props: Pick<ViewProps, 'children'>,
) => {
  const challengeRewardsContext = useChallengeRewards();
  const rewardsContext = useRewards();
  const challengesContext = useChallenges();

  const value = useMemo(
    () => ({
      ...challengeRewardsContext,
      ...rewardsContext,
      ...challengesContext,
    }),
    [challengeRewardsContext, rewardsContext, challengesContext],
  );

  return (
    <ChallengesAndRewardsContext.Provider value={value}>
      {props.children}
    </ChallengesAndRewardsContext.Provider>
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

export const useChallengesAndRewards = () => {
  const context = useContext(ChallengesAndRewardsContext);

  if (context === undefined) {
    throw new Error(
      'useChallengesAndRewards must be used within a <ChallengesAndRewardsProvider>',
    );
  }

  return context;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ChallengesAndRewardsContextType = ReturnType<typeof useRewards> &
  ReturnType<typeof useChallengeRewards> &
  ReturnType<typeof useChallenges>;
